import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../layouts/Layout';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import buildingIcon from '../assets/img3d/building.png';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom hook for debounced value
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const PropertyListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  
  // Check if user is a client
  const isClient = isAuthenticated && user?.role?.toLowerCase() === 'client';

  // Filter states
  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    transaction_type: searchParams.get('type') || '',
    property_type: '',
    min_price: '',
    max_price: '',
    min_rooms: '',
    min_surface: '',
    max_surface: '',
  });

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    return Object.values(filters).filter(value => value !== '').length;
  }, [filters]);

  // Check if any filters are active
  const hasActiveFilters = activeFiltersCount > 0;

  // Debounce text inputs (city, prices) to avoid too many API calls
  const debouncedCity = useDebounce(filters.city, 500);
  const debouncedMinPrice = useDebounce(filters.min_price, 500);
  const debouncedMaxPrice = useDebounce(filters.max_price, 500);

  // Auto-fetch when filters change (including initial mount)
  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        
        // String filters - convert to uppercase for enum values and use camelCase parameter names
        if (filters.transaction_type) {
          params.append('transactionType', filters.transaction_type.toUpperCase());
        }
        if (filters.property_type) {
          params.append('propertyType', filters.property_type.toUpperCase());
        }
        if (debouncedCity && debouncedCity.trim()) params.append('city', debouncedCity.trim());
        
        // Numeric filters - convert to numbers and use camelCase parameter names
        if (debouncedMinPrice && debouncedMinPrice.trim()) {
          const minPrice = parseFloat(debouncedMinPrice);
          if (!isNaN(minPrice) && minPrice > 0) {
            params.append('minPrice', minPrice.toString());
          }
        }
        
        if (debouncedMaxPrice && debouncedMaxPrice.trim()) {
          const maxPrice = parseFloat(debouncedMaxPrice);
          if (!isNaN(maxPrice) && maxPrice > 0) {
            params.append('maxPrice', maxPrice.toString());
          }
        }
        
        if (filters.min_rooms && filters.min_rooms.trim()) {
          const minRooms = parseInt(filters.min_rooms, 10);
          if (!isNaN(minRooms) && minRooms > 0) {
            params.append('minRooms', minRooms.toString());
          }
        }
        
        if (filters.min_surface && filters.min_surface.trim()) {
          const minSurface = parseFloat(filters.min_surface);
          if (!isNaN(minSurface) && minSurface > 0) {
            params.append('minSurface', minSurface.toString());
          }
        }
        
        if (filters.max_surface && filters.max_surface.trim()) {
          const maxSurface = parseFloat(filters.max_surface);
          if (!isNaN(maxSurface) && maxSurface > 0) {
            params.append('maxSurface', maxSurface.toString());
          }
        }

        setSearchParams(params);
        
        const response = await axios.get(`${API}/properties?${params.toString()}`);
        setProperties(response.data);
      } catch (error) {
        console.error('Error fetching properties:', error);
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [
    filters.transaction_type,
    filters.property_type,
    debouncedCity,
    debouncedMinPrice,
    debouncedMaxPrice,
    filters.min_rooms,
    filters.min_surface,
    filters.max_surface,
    setSearchParams,
  ]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Filters will auto-apply via useEffect
  };

  const resetFilters = () => {
    setFilters({
      city: '',
      transaction_type: '',
      property_type: '',
      min_price: '',
      max_price: '',
      min_rooms: '',
      min_surface: '',
      max_surface: '',
    });
    setSearchParams({});
    // Filters will auto-apply via useEffect
  };

  // Content to render (filters + properties grid)
  const content = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Horizontal Filters Bar - Épuré Design */}
        <div className={`mb-6 transition-all duration-300 ease-out overflow-hidden
          ${showFilters ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200/60 
            shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5">
            
            {/* Filters Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                  Filtres
                </h2>
                {activeFiltersCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-500 text-white rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <button
                    onClick={resetFilters}
                    className="text-xs font-medium text-gray-400 hover:text-red-500 
                      transition-colors duration-200"
                  >
                    Réinitialiser
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Filters Grid - Responsive Horizontal Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {/* Transaction Type */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction
                </label>
                <Select
                  value={filters.transaction_type}
                  onValueChange={(value) => handleFilterChange('transaction_type', value)}
                >
                  <SelectTrigger className="h-10 rounded-xl border-gray-200 bg-gray-50/50 
                    focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100
                    transition-all duration-200 text-sm">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Vente</SelectItem>
                    <SelectItem value="rent">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Property Type */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type de bien
                </label>
                <Select
                  value={filters.property_type}
                  onValueChange={(value) => handleFilterChange('property_type', value)}
                >
                  <SelectTrigger className="h-10 rounded-xl border-gray-200 bg-gray-50/50 
                    focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100
                    transition-all duration-200 text-sm">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Appartement</SelectItem>
                    <SelectItem value="house">Maison</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="loft">Loft</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* City */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ville
                </label>
                <Input
                  placeholder="Ex: Paris, Lyon..."
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  className="h-10 rounded-xl border-gray-200 bg-gray-50/50 
                    focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100
                    placeholder:text-gray-400 transition-all duration-200 text-sm"
                />
              </div>

              {/* Price Range */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix min
                </label>
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.min_price}
                  onChange={(e) => handleFilterChange('min_price', e.target.value)}
                  className="h-10 rounded-xl border-gray-200 bg-gray-50/50 
                    focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100
                    placeholder:text-gray-400 transition-all duration-200 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prix max
                </label>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.max_price}
                  onChange={(e) => handleFilterChange('max_price', e.target.value)}
                  className="h-10 rounded-xl border-gray-200 bg-gray-50/50 
                    focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100
                    placeholder:text-gray-400 transition-all duration-200 text-sm"
                />
              </div>

              {/* Rooms */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pièces min
                </label>
                <Select
                  value={filters.min_rooms}
                  onValueChange={(value) => handleFilterChange('min_rooms', value)}
                >
                  <SelectTrigger className="h-10 rounded-xl border-gray-200 bg-gray-50/50 
                    focus:bg-white focus:border-blue-300 focus:ring-2 focus:ring-blue-100
                    transition-all duration-200 text-sm">
                    <SelectValue placeholder="Tous" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1+</SelectItem>
                    <SelectItem value="2">2+</SelectItem>
                    <SelectItem value="3">3+</SelectItem>
                    <SelectItem value="4">4+</SelectItem>
                    <SelectItem value="5">5+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

          </div>
        </div>

        {/* Toggle Filters Button */}
        {!showFilters && (
          <div className="mb-6 flex justify-end">
            <button
              onClick={() => setShowFilters(true)}
              className="group flex items-center gap-2.5 px-4 py-2.5 rounded-xl 
                bg-white/80 backdrop-blur-sm border border-gray-200/60
                hover:bg-white hover:border-gray-300 hover:shadow-md
                active:scale-[0.98]
                transition-all duration-300 ease-out"
            >
              <SlidersHorizontal className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Filtres</span>
              {activeFiltersCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-semibold bg-blue-500 text-white rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Properties Grid */}
        <div className="w-full">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="relative">
                  <div className="h-16 w-16 rounded-full border-2 border-gray-200"></div>
                  <div className="absolute top-0 left-0 h-16 w-16 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
                </div>
              </div>
            ) : properties.length > 0 ? (
              <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 transition-all duration-300">
                {properties.map((property, index) => (
                  <div 
                    key={property.id}
                    className="animate-in fade-in slide-in-from-bottom-4 duration-500"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <PropertyCard property={property} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full 
                  bg-gray-100 text-gray-400 mb-6">
                  <Search className="h-10 w-10" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Aucun bien trouvé
                </h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Essayez de modifier vos critères de recherche pour trouver plus de résultats
                </p>
                <Button
                  onClick={resetFilters}
                  variant="outline"
                  className="rounded-xl border-blue-200 text-blue-600 hover:bg-blue-50 
                    hover:border-blue-300 transition-all duration-200"
                >
                  <X className="h-4 w-4 mr-2" />
                  Réinitialiser les filtres
                </Button>
              </div>
            )}
        </div>
    </div>
  );

  // Configuration du header selon le type d'utilisateur
  const headerConfig = {
    icon: buildingIcon,
    iconColor: "bg-white",
    title: "Rechercher un Bien",
    subtitle: "Trouvez le bien immobilier qui correspond à vos critères",
    ...(isClient && {
      primaryAction: "Mon Espace",
      onPrimaryAction: () => navigate('/client'),
    }),
  };

  // Utiliser Layout avec WelcomeHeader pour tous les utilisateurs
  return (
    <Layout headerConfig={headerConfig}>
      {content}
    </Layout>
  );
};

export default PropertyListPage;
